import type { ReviewOutcomes } from '@/lib/practice/grades';
import { usePersistentSession } from '@/hooks/ui/usePersistentSession';

/** Persisted quiz run. `submission` is only ever 'idle' | 'done' (the
 *  in-flight 'submitting' state is component-local and never persisted). */
export interface PersistedQuizSession {
  signature: string;
  activeIndex: number;
  selectedAnswer: number | string | number[] | null;
  showExplanation: boolean;
  verdicts: Record<number, boolean>;
  completed: boolean;
  /** Fresh per attempt — dedup key for the LessonQuizAttempt row (Progress
   *  trend). Distinct from the daily FSRS key so every attempt is tracked. */
  attemptRowId: string;
  submission: 'idle' | 'done';
  /** Per-concept FSRS submit result (applied vs deduped), keyed by the item's
   *  concept_id. Populated on finish; drives the honest completed-screen copy. */
  reviewOutcomes: ReviewOutcomes;
  /** The answer submitted per question index - for the end screen's "Review
   *  mistakes" ("You said … · Answer …"). Optional: sessions saved before it
   *  existed have none, and the list then shows just the right answer. */
  answers?: Record<number, number | string | number[]>;
}

function freshQuizSession(signature: string): PersistedQuizSession {
  return {
    signature,
    activeIndex: 0,
    selectedAnswer: null,
    showExplanation: false,
    verdicts: {},
    completed: false,
    attemptRowId: crypto.randomUUID(),
    submission: 'idle',
    reviewOutcomes: {},
    answers: {},
  };
}

export function useQuizSession(
  lessonId: string,
  signature: string,
  ready: boolean
) {
  const [session, update, reset] = usePersistentSession<PersistedQuizSession>(
    `xonera:practice:quiz:${lessonId}`,
    freshQuizSession,
    signature,
    ready
  );
  return { session, update, reset };
}
